// src/components/sync/ProjectSyncRunsPanel.tsx
import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { FaChevronRight, FaSyncAlt } from "react-icons/fa";
import { useGetProjectSyncRunsQuery } from "../../openapi";
import { IProjectRun } from "@backend/src/shared/schemas/schemas";

type Props = {
  projectNo: number;
  limit?: number;
};

function formatDateTime(dt: string | Date | undefined): string {
  if (!dt) return "";
  const d = dt instanceof Date ? dt : new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);
  return d.toLocaleString();
}

function formatSeconds(ms: number | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return "";
  return `${Math.round(ms / 1000)} s`;
}

function badgeColorForRunStatus(status: IProjectRun["status"]) {
  switch (status) {
    case "succeeded":
      return "green";
    case "failed":
      return "red";
    case "running":
    default:
      return "blue";
  }
}

function labelForRunStatus(status: IProjectRun["status"]) {
  switch (status) {
    case "succeeded":
      return "Vellykket";
    case "failed":
      return "Mislykket";
    case "running":
    default:
      return "Pågår";
  }
}

function labelForTrigger(trigger: IProjectRun["trigger"]) {
  switch (trigger) {
    case "manual":
      return "Manuell";
    case "scheduled":
      return "Planlagt";
    case "queue":
      return "Kø";
    default:
      return String(trigger);
  }
}

type StepStatus = NonNullable<IProjectRun["steps"]>[keyof IProjectRun["steps"]] extends infer S
  ? S extends { status: infer T }
    ? T
    : never
  : never;

function stepColor(status: StepStatus | undefined) {
  switch (status) {
    case "succeeded":
      return "green";
    case "failed":
      return "red";
    case "running":
      return "blue";
    case "skipped":
      return "gray";
    case "pending":
    default:
      return "gray";
  }
}

function stepStatusLabel(status: StepStatus | undefined) {
  switch (status) {
    case "succeeded":
      return "OK";
    case "failed":
      return "Feil";
    case "running":
      return "Pågår";
    case "skipped":
      return "Hoppet over";
    case "pending":
    default:
      return "Venter";
  }
}

type StepName =
  | "roomsSync"
  | "specifiedArticlesSync"
  | "mcbimItemsSnapshot"
  | "enqueueEnrich"
  | "enrich";

function StepTitle({ name }: { name: StepName }) {
  const text =
    name === "roomsSync"
      ? "Synk rom"
      : name === "specifiedArticlesSync"
        ? "RFP-artikler"
        : name === "mcbimItemsSnapshot"
          ? "MCBIM snapshot"
          : name === "enqueueEnrich"
            ? "Legger i kø"
            : "Beriking";

  return (
    <Text fw={700} size="sm">
      {text}
    </Text>
  );
}

function StatLine({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Group justify="space-between" gap="md">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={600}>
        {value}
      </Text>
    </Group>
  );
}

type RoomsSyncStep = NonNullable<IProjectRun["steps"]>["roomsSync"];
type SpecifiedArticlesSyncStep = NonNullable<IProjectRun["steps"]>["specifiedArticlesSync"];
type McBimItemsSnapshotStep = NonNullable<IProjectRun["steps"]>["mcbimItemsSnapshot"];
type EnqueueEnrichStep = NonNullable<IProjectRun["steps"]>["enqueueEnrich"];
type EnrichStep = NonNullable<IProjectRun["steps"]>["enrich"];

function RoomsSyncDetails({ step }: { step: RoomsSyncStep }) {
  if (!step) return null;

  return (
    <Card withBorder radius="md" p="sm">
      <Group justify="space-between" align="center" mb={6}>
        <StepTitle name="roomsSync" />
        <Badge variant="light" color={stepColor(step.status)}>
          {stepStatusLabel(step.status)}
        </Badge>
      </Group>

      <Stack gap={6}>
        {step.startedAt && <StatLine label="Start" value={formatDateTime(step.startedAt)} />}
        {step.finishedAt && <StatLine label="Ferdig" value={formatDateTime(step.finishedAt)} />}

        {step.result?.stats && (
          <>
            <Divider my={6} />
            <StatLine label="IFC spaces" value={step.result.stats.ifcSpacesFetched} />
            <StatLine label="RFP rooms" value={step.result.stats.rfpFetched} />
            <StatLine label="RFP-feil" value={String(step.result.stats.rfpFetchFailed)} />
            <StatLine label="Unike rom" value={step.result.stats.uniqueRoomNumbers} />
            <StatLine label="Nullstilte flagg" value={step.result.stats.flagsResetRoomsMatched} />
            <StatLine label="Upserts" value={step.result.stats.upsertOps} />
          </>
        )}

        {step.error?.message && (
          <>
            <Divider my={6} />
            <Text size="sm" fw={600} c="red">
              {step.error.message}
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
}

function SpecifiedArticlesSyncDetails({ step }: { step: SpecifiedArticlesSyncStep }) {
  if (!step) return null;

  return (
    <Card withBorder radius="md" p="sm">
      <Group justify="space-between" align="center" mb={6}>
        <StepTitle name="specifiedArticlesSync" />
        <Badge variant="light" color={stepColor(step.status)}>
          {stepStatusLabel(step.status)}
        </Badge>
      </Group>

      <Stack gap={6}>
        {step.startedAt && <StatLine label="Start" value={formatDateTime(step.startedAt)} />}
        {step.finishedAt && <StatLine label="Ferdig" value={formatDateTime(step.finishedAt)} />}

        {step.result && (
          <>
            <Divider my={6} />
            {"fetchedRows" in step.result && (
              <StatLine label="Rader" value={(step.result as any).fetchedRows} />
            )}
            {"uniqueRoomNumbers" in step.result && (
              <StatLine label="Unike rom" value={(step.result as any).uniqueRoomNumbers} />
            )}
            {"resetMatchedRooms" in step.result && (
              <StatLine label="Nullstilte rom" value={(step.result as any).resetMatchedRooms} />
            )}
            {"bulkOps" in step.result && (
              <StatLine label="Upserts" value={(step.result as any).bulkOps} />
            )}
          </>
        )}

        {step.error?.message && (
          <>
            <Divider my={6} />
            <Text size="sm" fw={600} c="red">
              {step.error.message}
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
}

function McBimItemsSnapshotDetails({ step }: { step: McBimItemsSnapshotStep }) {
  if (!step) return null;

  const r = step.result;

  return (
    <Card withBorder radius="md" p="sm">
      <Group justify="space-between" align="center" mb={6}>
        <StepTitle name="mcbimItemsSnapshot" />
        <Badge variant="light" color={stepColor(step.status)}>
          {stepStatusLabel(step.status)}
        </Badge>
      </Group>

      <Stack gap={6}>
        {step.startedAt && <StatLine label="Start" value={formatDateTime(step.startedAt)} />}
        {step.finishedAt && <StatLine label="Ferdig" value={formatDateTime(step.finishedAt)} />}

        {r && (
          <>
            <Divider my={6} />

            {typeof r.totalExpected === "number" && (
              <StatLine label="Totalt" value={r.totalExpected} />
            )}

            {typeof r.totalRooms === "number" && (
              <StatLine label="Prosessert" value={r.totalRooms} />
            )}

            {typeof r.ok === "number" && <StatLine label="OK" value={r.ok} />}
            {typeof r.skipped === "number" && <StatLine label="Hoppet over" value={r.skipped} />}
            {typeof r.failed === "number" && <StatLine label="Feil" value={r.failed} />}

            {typeof r.lastBatchMs === "number" && (
              <StatLine label="Siste batch" value={formatSeconds(r.lastBatchMs)} />
            )}

            {typeof r.avgMsPerRoom === "number" && (
              <StatLine label="Snitt per rom" value={`${Math.round(r.avgMsPerRoom)} ms`} />
            )}

            {r.lastUpdatedAt && (
              <StatLine label="Sist oppdatert" value={formatDateTime(r.lastUpdatedAt)} />
            )}
          </>
        )}

        {step.error?.message && (
          <>
            <Divider my={6} />
            <Text size="sm" fw={600} c="red">
              {step.error.message}
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
}

function EnqueueEnrichDetails({ step }: { step: EnqueueEnrichStep }) {
  if (!step) return null;

  return (
    <Card withBorder radius="md" p="sm">
      <Group justify="space-between" align="center" mb={6}>
        <StepTitle name="enqueueEnrich" />
        <Badge variant="light" color={stepColor(step.status)}>
          {stepStatusLabel(step.status)}
        </Badge>
      </Group>

      <Stack gap={6}>
        {step.startedAt && <StatLine label="Start" value={formatDateTime(step.startedAt)} />}
        {step.finishedAt && <StatLine label="Ferdig" value={formatDateTime(step.finishedAt)} />}

        {typeof step.queuedBatches === "number" && (
          <StatLine label="Batcher" value={step.queuedBatches} />
        )}
        {typeof step.totalRooms === "number" && <StatLine label="Rom" value={step.totalRooms} />}

        {Array.isArray(step.jobTypes) && step.jobTypes.length > 0 && (
          <StatLine label="Jobbtyper" value={step.jobTypes.join(", ")} />
        )}

        {step.error?.message && (
          <>
            <Divider my={6} />
            <Text size="sm" fw={600} c="red">
              {step.error.message}
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
}

function EnrichDetails({ step }: { step: EnrichStep }) {
  if (!step) return null;

  return (
    <Card withBorder radius="md" p="sm">
      <Group justify="space-between" align="center" mb={6}>
        <StepTitle name="enrich" />
        <Badge variant="light" color={stepColor(step.status)}>
          {stepStatusLabel(step.status)}
        </Badge>
      </Group>

      <Stack gap={6}>
        {step.startedAt && <StatLine label="Start" value={formatDateTime(step.startedAt)} />}
        {step.finishedAt && <StatLine label="Ferdig" value={formatDateTime(step.finishedAt)} />}

        {typeof step.total === "number" && <StatLine label="Totalt" value={step.total} />}
        {typeof step.done === "number" && <StatLine label="Ferdig" value={step.done} />}
        {typeof step.failed === "number" && <StatLine label="Feil" value={step.failed} />}

        {step.lastUpdatedAt && (
          <StatLine label="Sist oppdatert" value={formatDateTime(step.lastUpdatedAt)} />
        )}

        {step.error?.message && (
          <>
            <Divider my={6} />
            <Text size="sm" fw={600} c="red">
              {step.error.message}
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
}

export const ProjectSyncRunsPanel: React.FC<Props> = ({ projectNo, limit = 50 }) => {
  const [selected, setSelected] = useState<IProjectRun | null>(null);

  const { data, isLoading, isFetching, isError, error, refetch } = useGetProjectSyncRunsQuery(
    { projectNo, limit },
    { refetchOnMountOrArgChange: true }
  );

  const runs = useMemo(() => data?.items ?? [], [data]);

  return (
    <Card withBorder radius="md" p="md">
      <Group justify="space-between" align="center" mb="sm">
        <Group gap="xs">
          <Title order={4}>Synk-kjøringer</Title>
          {isFetching && <Loader size="xs" />}
        </Group>

        <Tooltip label="Oppdater">
          <Button
            variant="light"
            leftSection={<FaSyncAlt size={14} />}
            onClick={() => refetch()}
            size="xs"
            loading={isFetching}
          >
            Oppdater
          </Button>
        </Tooltip>
      </Group>

      {isError && (
        <Text c="red" size="sm" mb="sm">
          {String(
            (error as { data?: { message?: string }; error?: string })?.data?.message ??
              (error as { error?: string })?.error ??
              "Kunne ikke hente kjøringer"
          )}
        </Text>
      )}

      {isLoading ? (
        <Group justify="center" py="lg">
          <Loader />
        </Group>
      ) : runs.length === 0 ? (
        <Text c="dimmed" size="sm">
          Ingen synk-kjøringer funnet for prosjektet.
        </Text>
      ) : (
        <ScrollArea h={320} type="auto">
          <Stack gap="xs">
            {runs.map((r) => {
              const roomsSync = r.steps.roomsSync;
              const specifiedArticlesSync = r.steps.specifiedArticlesSync;
              const mcbimItemsSnapshot = r.steps.mcbimItemsSnapshot;
              const enqueueEnrich = r.steps.enqueueEnrich;
              const enrich = r.steps.enrich;

              const snapshotProgress =
                mcbimItemsSnapshot?.result &&
                typeof mcbimItemsSnapshot.result.totalRooms === "number" &&
                typeof mcbimItemsSnapshot.result.totalExpected === "number" &&
                mcbimItemsSnapshot.result.totalExpected > 0
                  ? `${mcbimItemsSnapshot.result.totalRooms}/${mcbimItemsSnapshot.result.totalExpected}`
                  : null;

              return (
                <Card
                  key={String(r._id ?? r.createdAt)}
                  withBorder
                  radius="md"
                  p="sm"
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelected(r)}
                >
                  <Group justify="space-between" align="center">
                    <Stack gap={6} style={{ minWidth: 0 }}>
                      <Group gap="xs" wrap="wrap">
                        <Badge color={badgeColorForRunStatus(r.status)} variant="filled" size="sm">
                          {labelForRunStatus(r.status)}
                        </Badge>

                        <Badge variant="light" size="sm">
                          {labelForTrigger(r.trigger)}
                        </Badge>

                        <Badge variant="light" color={stepColor(roomsSync?.status)} size="sm">
                          Synk rom: {stepStatusLabel(roomsSync?.status)}
                        </Badge>

                        <Badge
                          variant="light"
                          color={stepColor(specifiedArticlesSync?.status)}
                          size="sm"
                        >
                          RFP-artikler: {stepStatusLabel(specifiedArticlesSync?.status)}
                        </Badge>

                        <Badge
                          variant="light"
                          color={stepColor(mcbimItemsSnapshot?.status)}
                          size="sm"
                        >
                          MCBIM snapshot: {stepStatusLabel(mcbimItemsSnapshot?.status)}
                          {snapshotProgress ? ` (${snapshotProgress})` : ""}
                        </Badge>

                        <Badge variant="light" color={stepColor(enqueueEnrich?.status)} size="sm">
                          Kø: {stepStatusLabel(enqueueEnrich?.status)}
                        </Badge>

                        <Badge variant="light" color={stepColor(enrich?.status)} size="sm">
                          Beriking: {stepStatusLabel(enrich?.status)}
                        </Badge>
                      </Group>

                      <Text size="sm" fw={600} lineClamp={1}>
                        {formatDateTime(r.createdAt)}
                      </Text>

                      <Text size="xs" c="dimmed" lineClamp={1}>
                        Prosjekt {projectNo}
                        {r.meta?.invocationId ? ` • ${r.meta.invocationId}` : ""}
                      </Text>
                    </Stack>

                    <FaChevronRight size={16} />
                  </Group>
                </Card>
              );
            })}
          </Stack>
        </ScrollArea>
      )}

      <Modal
        opened={Boolean(selected)}
        onClose={() => setSelected(null)}
        title="Detaljer"
        size="lg"
        centered
      >
        {selected && (
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Group gap="xs">
                <Badge color={badgeColorForRunStatus(selected.status)} variant="filled">
                  {labelForRunStatus(selected.status)}
                </Badge>
                <Badge variant="light">{labelForTrigger(selected.trigger)}</Badge>
              </Group>

              <Text size="sm" c="dimmed">
                {formatDateTime(selected.createdAt)}
              </Text>
            </Group>

            <Divider />

            <Stack gap="xs">
              <StatLine label="Prosjekt" value={String(selected.projectNo)} />
              <StatLine label="Opprettet" value={formatDateTime(selected.createdAt)} />
              {selected.startedAt && (
                <StatLine label="Startet" value={formatDateTime(selected.startedAt)} />
              )}
              {selected.finishedAt && (
                <StatLine label="Ferdig" value={formatDateTime(selected.finishedAt)} />
              )}
              {selected.requestedBy && <StatLine label="Startet av" value={selected.requestedBy} />}
              {selected.correlationId && (
                <StatLine label="Correlation id" value={selected.correlationId} />
              )}
              {selected.meta?.functionName && (
                <StatLine label="Function" value={selected.meta.functionName} />
              )}
              {selected.meta?.invocationId && (
                <StatLine label="Invocation id" value={selected.meta.invocationId} />
              )}
            </Stack>

            <Divider label="Steg" />

            <Stack gap="xs">
              <RoomsSyncDetails step={selected.steps.roomsSync} />
              <SpecifiedArticlesSyncDetails step={selected.steps.specifiedArticlesSync} />
              <McBimItemsSnapshotDetails step={selected.steps.mcbimItemsSnapshot} />
              <EnqueueEnrichDetails step={selected.steps.enqueueEnrich} />
              <EnrichDetails step={selected.steps.enrich} />
            </Stack>
          </Stack>
        )}
      </Modal>
    </Card>
  );
};
